import React, { useEffect, useState, useMemo } from 'react'
import { useInfiniteQuery } from 'react-query'
import { Button, Modal } from 'components'
import { GroupDetailsItem } from '../components/StoredDeliveriesListDetails/StoredDeliveriesListDetailsItem'
import { handleDeliveryIcon } from '../components/consts'
import { toast } from 'react-toastify'
import {
  GroupModalContainer,
  GroupModalRows,
  GroupModalSubTitle,
} from './styled'
import { Entity, usePermissions } from '../../../utils/hooks'
import { useDeliveriesService } from '../../../services'
import { Stack, Tooltip } from '@mui/material'

type GroupDeliveriesProps = {
  onConfirm: (
    groupedIds: number[],
    editingGroupId: number | null
  ) => Promise<void>
  wheatCode: string
  wheatCodeId: number
}

const useGroupDeliveries = ({
  onConfirm,
  wheatCode,
  wheatCodeId,
}: GroupDeliveriesProps): [(groupId?: number) => void, React.ReactElement] => {
  const { checkPermission } = usePermissions()
  const { getStoredDeliveriesThin } = useDeliveriesService()
  const PAGE_LIMIT = 20

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ['fetch-grouping-deliveries', wheatCodeId],
      ({ pageParam = 0 }) =>
        getStoredDeliveriesThin(wheatCodeId, true, true, pageParam, PAGE_LIMIT),
      {
        getNextPageParam: (lastPage) => {
          const { offset, limit, total } = lastPage.pagination
          if (offset + limit < total) {
            return offset + limit
          }
          return undefined
        },
      }
    )

  const fullDeliveries = useMemo(
    () => data?.pages.flatMap((page) => page.results) || [],
    [data?.pages]
  )

  const [isOpen, setIsOpen] = useState(false)
  const [groupedIds, setGroupedIds] = useState<number[]>([])
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null)

  useEffect(() => {
    if (fullDeliveries && editingGroupId !== null) {
      const groupDeliveries = fullDeliveries
        .filter((item) => item.delivery_group?.id === editingGroupId)
        .map((item) => item.id)
      setGroupedIds(groupDeliveries)
    }
  }, [fullDeliveries, editingGroupId])

  const handleGroupDeliveries = () => {
    if (editingGroupId ? groupedIds.length === 1 : groupedIds.length < 2) {
      toast.error('Please select more than one delivery')
      return
    }

    void onConfirm(groupedIds, editingGroupId)
    setIsOpen(false)
  }

  const toggleGroupDelivery = (deliveryDetailId: number) => {
    setGroupedIds((prevIds) =>
      prevIds.includes(deliveryDetailId)
        ? prevIds.filter((id) => id !== deliveryDetailId)
        : [...prevIds, deliveryDetailId]
    )
  }

  const openModal = (groupId?: number) => {
    if (groupId) {
      setEditingGroupId(groupId)
      const groupDeliveries =
        fullDeliveries
          ?.filter((item) => item.delivery_group?.id === groupId)
          .map((item) => item.id) ?? []
      setGroupedIds(groupDeliveries)
    } else {
      setEditingGroupId(null)
      setGroupedIds([])
    }
    setIsOpen(true)
  }

  const closeModal = () => {
    setIsOpen(false)
    setEditingGroupId(null)
    setGroupedIds([])
  }

  const filteredDeliveries = fullDeliveries?.filter((item) =>
    editingGroupId
      ? item.delivery_group?.id === editingGroupId || !item?.delivery_group?.id
      : !item.delivery_group?.id
  )

  const isReadOnly = editingGroupId
    ? !checkPermission(Entity.DELIVERY, 'update_group')
    : !checkPermission(Entity.DELIVERY, 'create_group')

  return [
    openModal,
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      onConfirm={handleGroupDeliveries}
      onConfirmButtonText="Group selected deliveries"
      confirmButtonDisabled={filteredDeliveries?.length < 1 || isReadOnly}
      title={
        editingGroupId
          ? `Edit Group ${editingGroupId} | WheatCode ${wheatCode}`
          : `New Group`
      }
    >
      <GroupModalContainer>
        <GroupModalSubTitle>Select deliveries to group</GroupModalSubTitle>
        <GroupModalRows>
          {fullDeliveries?.map((deliveryDetail) => {
            const Icon = handleDeliveryIcon(deliveryDetail)
            const acceptedBin = deliveryDetail.accepted_bin?.name
            const belongsToOtherGroup =
              deliveryDetail?.delivery_group?.id !== editingGroupId &&
              !!deliveryDetail?.delivery_group?.id

            if (belongsToOtherGroup) {
              return (
                <Tooltip
                  placement="left"
                  title={`${deliveryDetail.delivery_group?.group_name}`}
                  key={deliveryDetail.id}
                >
                  <div style={{ width: '100%' }}>
                    <GroupDetailsItem
                      key={deliveryDetail.id}
                      date={deliveryDetail?.nir?.arrival_date}
                      binName={acceptedBin}
                      icon={Icon}
                      {...deliveryDetail}
                      grouped={true}
                      disabled={belongsToOtherGroup}
                    />
                  </div>
                </Tooltip>
              )
            } else {
              return (
                <GroupDetailsItem
                  key={deliveryDetail.id}
                  date={deliveryDetail?.nir?.arrival_date}
                  binName={acceptedBin}
                  icon={Icon}
                  onClick={() => toggleGroupDelivery(deliveryDetail.id)}
                  {...deliveryDetail}
                  grouped={groupedIds.includes(deliveryDetail.id)}
                  disabled={isReadOnly}
                />
              )
            }
          })}

          {hasNextPage && (
            <Stack
              sx={{
                alignItems: 'center',
              }}
            >
              <Button
                size="small"
                sx={{
                  width: '140px',
                }}
                onClick={() => {
                  if (!isFetchingNextPage) {
                    void fetchNextPage()
                  }
                }}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </Stack>
          )}
        </GroupModalRows>
      </GroupModalContainer>
    </Modal>,
  ]
}

export default useGroupDeliveries
